// src/pages/Procurement/ProcurementDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/axios";
import ProcurementFilterBar from "@/components/Procurement/ProcurementFilterBar";
import NewPOForm from "@/components/Procurement/NewPOForm";
import ProcurementAIInsightsPanel from "@/components/Procurement/ProcurementAIInsightsPanel";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { procurementContextKey } from "@/lib/procurementAssistantRequests";
import { validateDashboardSummary, dashboardMoneyMetric } from "@/lib/procurementDashboardCurrency";
import { useProcurementOperationalContext } from "@/hooks/useProcurementOperationalContext";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  Boxes,
  BrainCircuit,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  FilePlus2,
  FileText,
  PackageCheck,
  PackageOpen,
  RefreshCw,
  ShoppingCart,
  Sparkles,
  Truck,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const ROUTES = {
  PROCUREMENT_DASH: "/procurement",
  PROCUREMENT_AI_INSIGHTS: "/procurement/ai-insights",
  PROCUREMENT_GM_BRIEF: "/procurement/gm-brief",
  PROCUREMENT_GM_ASSISTANT: "/procurement/gm-assistant",
  PO_MANAGEMENT: "/po-management",
  PO_RECEIVING_DASH: "/po-receiving-dashboard",
  PO_DETAILS: (poId) => `/procurement/po-details/${poId}`,
  PO_RECEIVE: (poId) => `/po-receiving/${poId}`,
  PO_APPROVAL_CENTRE: "/procurement/po-approval",
  DISPATCHED_POS: "/dispatched-pos",
  INVOICES: "/invoices",
  VENDORS: "/vendors",
  INVENTORY_DASH: "/inventory",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10) || "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function computeSummaryFromPOs(rows, todayStr = new Date().toISOString().slice(0, 10)) {

  const totalPOs = rows.length;
  const unapproved = rows.filter((r) =>
    (r.status || "").toLowerCase().includes("pending")
  ).length;
  const partial = rows.filter((r) =>
    (r.status || "").toLowerCase().includes("partially")
  ).length;
  const fullyReceived = rows.filter((r) =>
    (r.status || "").toLowerCase().includes("fully")
  ).length;

  const arrivingToday = rows.filter(
    (r) => (r.expected_delivery || "").slice(0, 10) === todayStr
  );

  return {
    totalPOs,
    unapproved,
    partial,
    fullyReceived,
    arrivingTodayCount: arrivingToday.length,
  };
}

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "group inline-flex min-w-fit items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all",
      active
        ? "bg-white text-slate-950 shadow-sm ring-1 ring-white/80"
        : "text-slate-300 hover:bg-white/10 hover:text-white",
    ].join(" ")}
  >
    <Icon size={16} className={active ? "text-cyan-600" : "text-slate-400 group-hover:text-white"} />
    {label}
  </button>
);

const MetricCard = ({ label, value, hint, icon: Icon, tone = "slate", to }) => {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50/70 text-emerald-700",
    amber: "border-amber-200 bg-amber-50/70 text-amber-700",
    cyan: "border-cyan-200 bg-cyan-50/70 text-cyan-700",
    rose: "border-rose-200 bg-rose-50/70 text-rose-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };

  const content = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
          <p className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{value}</p>
          <p className="mt-1 break-words text-xs text-slate-500">{hint}</p>
        </div>
        <div className={`rounded-xl border p-2.5 ${toneMap[tone] || toneMap.slate}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );

  return to ? <Link to={to}>{content}</Link> : content;
};

const QuickAction = ({ to, icon: Icon, title, description }) => (
  <Link
    to={to}
    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md"
  >
    <div className="rounded-xl bg-slate-950 p-2.5 text-cyan-300 transition group-hover:bg-cyan-950">
      <Icon size={18} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-950">{title}</p>
        <ArrowRight size={15} className="text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-cyan-600" />
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
    </div>
  </Link>
);

const Section = ({ title, subtitle, right, children }) => (
  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {right ? <div>{right}</div> : null}
    </div>
    <div className="p-6">{children}</div>
  </section>
);

export default function ProcurementDashboard() {
  const permissions = useUserPermissions();
  const operational = useProcurementOperationalContext(permissions.user, permissions.authenticated && permissions.canAccessProcurementDashboard);
  const key = procurementContextKey(operational.expected);
  if (!permissions.authenticated || !permissions.canAccessProcurementDashboard) {
    return <div className="p-6 text-rose-600"><h2 className="mb-2 text-xl font-bold">Access Denied</h2>
      <p>You do not have access to the Procurement Dashboard.</p></div>;
  }
  if (operational.loading) return <div className="p-6">Loading selected company and unit…</div>;
  if (operational.error) return <div className="p-6 text-rose-600" role="alert">{operational.error}</div>;
  if (!key) return <div className="p-6">Select your unit and refresh your session to open the dashboard.</div>;
  return <>
    {operational.refreshing && <div className="p-6" role="status">Checking selected company and unit…</div>}
    <div hidden={operational.refreshing} inert={operational.refreshing ? '' : undefined}>
      <ProcurementDashboardWorkspace key={key} permissions={{...permissions,user:operational.expected}} />
    </div>
  </>;
}

function ProcurementDashboardWorkspace({ permissions }) {
  const {
    user,
    canAccessProcurementDashboard,
    isFinance,
    isAdmin,
    isDeveloper,
    isManager,
  } = permissions;

  const canViewProcurementAI =
    Boolean(isAdmin) || Boolean(isManager) || Boolean(isDeveloper);

  const canViewGmCommand =
    Boolean(isAdmin) ||
    Boolean(isDeveloper) ||
    Boolean(isManager) ||
    Boolean(isFinance);

  const [activeView, setActiveView] = useState("overview");
  const [filters, setFilters] = useState({ vendor: "", status: "", fromDate: "" });
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dataDenied, setDataDenied] = useState(false);
  const [vendorPerf, setVendorPerf] = useState([]);
  const [refreshMs, setRefreshMs] = useState(15000);
  const timerRef = useRef(null);
  const alive = useRef(true), pending = useRef({});
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; Object.values(pending.current).forEach(c => c.abort()); };
  }, []);
  const begin = name => {
    pending.current[name]?.abort();
    const controller = new AbortController(); pending.current[name] = controller; return controller;
  };
  const current = (name, controller) => alive.current && !controller.signal.aborted && pending.current[name] === controller;
  const denyIfNeeded = (error, resource) => {
    if (error?.response?.status === 401 || (resource === "pos" && error?.response?.status === 403) ||
        error?.code === "PROCUREMENT_SUMMARY_CONTEXT_CHANGED") {
      setDataDenied(true); setSummary(null); setPurchaseOrders([]); setVendorPerf([]);
      Object.values(pending.current).forEach(c => c.abort());
    }
  };

  const fetchPOs = async () => {
    const controller = begin("pos");
    try {
      const res = await api.get("/purchase-orders/all", { signal: controller.signal });
      if (!current("pos", controller)) return;
      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];

      setPurchaseOrders(rows);
    } catch (err) {
      if (current("pos", controller)) { setPurchaseOrders([]); denyIfNeeded(err, "pos"); }
    } finally {
      if (current("pos", controller)) setLoading(false);
    }
  };

  const fetchSummary = async () => {
    const controller = begin("summary");
    try {
      const res = await api.get("/analytics/procurement/summary", { signal: controller.signal });
      if (current("summary", controller)) setSummary(validateDashboardSummary(res.data, user));
    } catch (error) {
      if (current("summary", controller)) { setSummary(null); denyIfNeeded(error, "summary"); }
    }
  };

  const fetchVendorPerformance = async () => {
    const controller = begin("vendors");
    try {
      const res = await api.get("/analytics/vendor-performance", { signal: controller.signal });
      if (current("vendors", controller)) setVendorPerf(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      if (current("vendors", controller)) { setVendorPerf([]); denyIfNeeded(error, "vendors"); }
    }
  };

  useEffect(() => {
    if (!canAccessProcurementDashboard || dataDenied) return;
    fetchPOs();
    fetchSummary();
    fetchVendorPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessProcurementDashboard, dataDenied]);

  useEffect(() => {
    if (!canAccessProcurementDashboard || dataDenied) return;

    clearInterval(timerRef.current);

    if (refreshMs > 0) {
      timerRef.current = setInterval(() => {
        fetchSummary();
        fetchPOs();
        fetchVendorPerformance();
      }, refreshMs);
    }

    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs, canAccessProcurementDashboard, dataDenied]);

  const filteredOrders = useMemo(() => {
    return purchaseOrders.filter((order) => {
      const vendorMatch =
        !filters.vendor ||
        order.vendor_name?.toLowerCase().includes(filters.vendor.toLowerCase());

      const statusMatch = !filters.status || order.status === filters.status;

      const dateMatch =
        !filters.fromDate ||
        (order.created_at && new Date(order.created_at) >= new Date(filters.fromDate));

      return vendorMatch && statusMatch && dateMatch;
    });
  }, [purchaseOrders, filters]);

  const derived = useMemo(() => computeSummaryFromPOs(filteredOrders, summary?.as_of_date), [filteredOrders, summary?.as_of_date]);

  const operationalMetrics = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const overdue = purchaseOrders.filter((po) => {
      if (!po?.expected_delivery) return false;
      const status = String(po.status || "").toLowerCase();
      if (status.includes("fully") || status.includes("cancel") || status.includes("reject")) {
        return false;
      }
      const due = new Date(po.expected_delivery);
      if (Number.isNaN(due.getTime())) return false;
      due.setHours(0, 0, 0, 0);
      return due < now;
    });

    const today = summary?.as_of_date || new Date().toISOString().slice(0, 10);
    const dueToday = purchaseOrders.filter(
      (po) => String(po?.expected_delivery || "").slice(0, 10) === today
    );

    const recent = [...purchaseOrders]
      .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
      .slice(0, 6);

    return { overdue, dueToday, recent };
  }, [purchaseOrders, summary?.as_of_date]);

  const S = summary?.totals || {};
  const receivedCount = S.received_pos ?? derived.fullyReceived;
  const arrivingTodayCount = S.arriving_today_count ?? derived.arrivingTodayCount;
  const arrivingTodayMoney = dashboardMoneyMetric(summary, "arriving_today");
  const unapprovedCount = S.unapproved_pos ?? derived.unapproved;
  const partialCount = S.partial_pos ?? derived.partial;
  const raisedWeekMoney = dashboardMoneyMetric(summary, "raised_week_pending");

  const tabs = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "orders", label: "Purchase Orders", icon: ShoppingCart },
    { id: "raise", label: "Raise PO", icon: FilePlus2 },
    { id: "receiving", label: "Receiving", icon: PackageCheck },
    { id: "vendors", label: "Vendors", icon: Building2 },
    ...(canViewProcurementAI
      ? [{ id: "intelligence", label: "Intelligence", icon: BrainCircuit }]
      : []),
    ...(canViewGmCommand
      ? [{ id: "executive", label: "Executive", icon: Sparkles }]
      : []),
  ];

  if (dataDenied) {
    return (
      <div className="p-6 text-rose-600">
        <h2 className="mb-2 text-xl font-bold">Access Denied</h2>
        <p>You do not have access to the Procurement Dashboard.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="mx-auto max-w-[1680px] px-4 py-5 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[30px] border border-slate-800 bg-slate-950 shadow-2xl shadow-slate-300/40">
          <div className="relative overflow-hidden px-6 py-7 md:px-8">
            <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

            <div className="relative flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-200">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
                    LIVE OPERATIONS
                  </span>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">
                    Procurement Command Centre
                  </span>
                </div>

                <h1 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                  Procurement Command Centre
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
                  One operational view for purchasing, approvals, deliveries, supplier performance,
                  intelligence and executive decision support.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-400">
                  <RefreshCw size={14} />
                  <span>Auto-refresh</span>
                  <select
                    value={refreshMs}
                    onChange={(e) => setRefreshMs(Number(e.target.value))}
                    className="rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-slate-200 outline-none"
                  >
                    <option value={15000}>15s</option>
                    <option value={30000}>30s</option>
                    <option value={60000}>60s</option>
                    <option value={0}>Off</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    fetchSummary();
                    fetchPOs();
                    fetchVendorPerformance();
                  }}
                  className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
                >
                  <RefreshCw size={14} />
                  Refresh now
                </button>
              </div>
            </div>

            <div className="relative mt-7 overflow-x-auto pb-1">
              <div className="flex min-w-max items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5">
                {tabs.map((tab) => (
                  <TabButton
                    key={tab.id}
                    active={activeView === tab.id}
                    icon={tab.icon}
                    label={tab.label}
                    onClick={() => setActiveView(tab.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          {activeView === "overview" && (
            <div className="space-y-6">
              <p className="text-xs text-slate-500">Financial totals cover all permitted POs in this unit. Order filters apply to the order list.</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  label="Pending Approval"
                  value={unapprovedCount}
                  hint="Purchase orders awaiting authority"
                  icon={ClipboardCheck}
                  tone="amber"
                  to={`${ROUTES.PO_MANAGEMENT}?tab=pending`}
                />
                <MetricCard
                  label="Due Today"
                  value={arrivingTodayCount}
                  hint={`${arrivingTodayMoney.value} · ${arrivingTodayMoney.hint}`}
                  icon={Truck}
                  tone="cyan"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=today`}
                />
                <MetricCard
                  label="Partially Received"
                  value={partialCount}
                  hint="Orders still awaiting balance"
                  icon={PackageOpen}
                  tone="rose"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=partial`}
                />
                <MetricCard
                  label="Fully Received"
                  value={receivedCount}
                  hint="Completed receiving lifecycle"
                  icon={CheckCircle2}
                  tone="emerald"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=received`}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                <section className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-xl">
                  <div className="border-b border-white/10 px-6 py-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                          <Activity size={15} /> Operational display
                        </div>
                        <h2 className="mt-2 text-2xl font-semibold text-white">Today's procurement position</h2>
                      </div>
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-400">
                        {purchaseOrders.length} purchase orders loaded
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="bg-slate-950 p-5">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Overdue deliveries</p>
                      <p className="mt-2 text-3xl font-bold text-rose-300">{operationalMetrics.overdue.length}</p>
                      <p className="mt-1 text-xs text-slate-500">Requires operational follow-up</p>
                    </div>
                    <div className="bg-slate-950 p-5">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Pending this week</p>
                      <p className="mt-2 break-words text-2xl font-bold text-amber-300">{raisedWeekMoney.value}</p>
                      <p className="mt-1 text-xs text-slate-500">{raisedWeekMoney.hint}</p>
                    </div>
                    <div className="bg-slate-950 p-5">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Due today</p>
                      <p className="mt-2 text-3xl font-bold text-cyan-300">{operationalMetrics.dueToday.length}</p>
                      <p className="mt-1 text-xs text-slate-500">Expected supplier deliveries</p>
                    </div>
                    <div className="bg-slate-950 p-5">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-500">PO population</p>
                      <p className="mt-2 text-3xl font-bold text-white">{derived.totalPOs}</p>
                      <p className="mt-1 text-xs text-slate-500">Current filtered operational set</p>
                    </div>
                  </div>

                  <div className="border-t border-white/10 px-6 py-5">
                    <div className="grid gap-3 md:grid-cols-3">
                      <button
                        type="button"
                        onClick={() => setActiveView("raise")}
                        className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-cyan-400/30 hover:bg-cyan-400/10"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">Raise Purchase Order</span>
                          <span className="mt-1 block text-xs text-slate-500">Start a new operational order</span>
                        </span>
                        <FilePlus2 size={18} className="text-cyan-300" />
                      </button>

                      <Link
                        to={ROUTES.PO_APPROVAL_CENTRE}
                        className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-4 transition hover:border-amber-400/30 hover:bg-amber-400/10"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">Approval Centre</span>
                          <span className="mt-1 block text-xs text-slate-500">Review pending authority</span>
                        </span>
                        <ClipboardCheck size={18} className="text-amber-300" />
                      </Link>

                      <button
                        type="button"
                        onClick={() => setActiveView("receiving")}
                        className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-emerald-400/30 hover:bg-emerald-400/10"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">Receiving</span>
                          <span className="mt-1 block text-xs text-slate-500">Process today's deliveries</span>
                        </span>
                        <PackageCheck size={18} className="text-emerald-300" />
                      </button>
                    </div>
                  </div>
                </section>

                <Section
                  title="Attention Required"
                  subtitle="The operational items most likely to need action now."
                  right={
                    <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                      <AlertTriangle size={14} /> Live
                    </span>
                  }
                >
                  <div className="space-y-3">
                    <Link
                      to={`${ROUTES.PO_MANAGEMENT}?tab=pending`}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-amber-300 hover:bg-amber-50/50"
                    >
                      <div>
                        <p className="font-semibold text-slate-950">Pending approvals</p>
                        <p className="mt-1 text-sm text-slate-500">Orders waiting for approval authority.</p>
                      </div>
                      <span className="text-2xl font-bold text-amber-600">{unapprovedCount}</span>
                    </Link>

                    <Link
                      to={ROUTES.PO_RECEIVING_DASH}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-rose-300 hover:bg-rose-50/50"
                    >
                      <div>
                        <p className="font-semibold text-slate-950">Overdue deliveries</p>
                        <p className="mt-1 text-sm text-slate-500">Expected dates have passed without completion.</p>
                      </div>
                      <span className="text-2xl font-bold text-rose-600">{operationalMetrics.overdue.length}</span>
                    </Link>

                    <Link
                      to={`${ROUTES.PO_RECEIVING_DASH}?filter=partial`}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-cyan-300 hover:bg-cyan-50/50"
                    >
                      <div>
                        <p className="font-semibold text-slate-950">Open receiving balances</p>
                        <p className="mt-1 text-sm text-slate-500">Partially received orders still in progress.</p>
                      </div>
                      <span className="text-2xl font-bold text-cyan-700">{partialCount}</span>
                    </Link>
                  </div>
                </Section>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.8fr_1.2fr]">
                <Section title="Quick Actions" subtitle="Move directly into the operational workflow you need.">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                    <QuickAction
                      to={ROUTES.PO_MANAGEMENT}
                      icon={ShoppingCart}
                      title="PO Management"
                      description="Review the full purchase order lifecycle."
                    />
                    <QuickAction
                      to={ROUTES.INVOICES}
                      icon={FileText}
                      title="Invoice Management"
                      description="Move from receiving into invoice control."
                    />
                    <QuickAction
                      to={ROUTES.VENDORS}
                      icon={Building2}
                      title="Vendor Directory"
                      description="Open supplier records and performance data."
                    />
                    <QuickAction
                      to={ROUTES.INVENTORY_DASH}
                      icon={Boxes}
                      title="Inventory"
                      description="Move into stock and replenishment operations."
                    />
                  </div>
                </Section>

                <Section
                  title="Latest Purchase Orders"
                  subtitle="Most recently created operational orders."
                  right={
                    <button
                      type="button"
                      onClick={() => setActiveView("orders")}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900"
                    >
                      View all <ArrowRight size={15} />
                    </button>
                  }
                >
                  {loading ? (
                    <p className="text-sm text-slate-500">Loading purchase orders…</p>
                  ) : operationalMetrics.recent.length === 0 ? (
                    <p className="text-sm text-slate-500">No purchase orders found.</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {operationalMetrics.recent.map((order) => (
                        <Link
                          key={order.po_id}
                          to={ROUTES.PO_DETAILS(order.po_id)}
                          className="flex flex-col gap-2 py-3 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:px-2"
                        >
                          <div>
                            <p className="font-semibold text-slate-950">{order.po_number || `PO-${order.po_id}`}</p>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {order.vendor_name || "Vendor not shown"} • {formatDate(order.created_at)}
                            </p>
                          </div>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                            {order.status || "—"}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </Section>
              </div>
            </div>
          )}

          {activeView === "orders" && (
            <div className="space-y-6">
              <Section
                title="Purchase Orders"
                subtitle="Search, filter and move directly into the purchase order lifecycle."
                right={
                  <Link
                    to={ROUTES.PO_MANAGEMENT}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Full PO Management <ArrowRight size={15} />
                  </Link>
                }
              >
                <ProcurementFilterBar filters={filters} setFilters={setFilters} />

                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-[900px] w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">PO Number</th>
                          <th className="px-4 py-3 text-left font-semibold">Vendor</th>
                          <th className="px-4 py-3 text-left font-semibold">Created</th>
                          <th className="px-4 py-3 text-left font-semibold">Expected</th>
                          <th className="px-4 py-3 text-left font-semibold">Status</th>
                          <th className="px-4 py-3 text-right font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {filteredOrders.map((order) => (
                          <tr key={order.po_id} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-semibold text-slate-950">
                              <Link to={ROUTES.PO_DETAILS(order.po_id)} className="hover:text-cyan-700">
                                {order.po_number || order.po_id}
                              </Link>
                            </td>
                            <td className="px-4 py-4 text-slate-700">{order.vendor_name || "—"}</td>
                            <td className="px-4 py-4 text-slate-600">{formatDate(order.created_at)}</td>
                            <td className="px-4 py-4 text-slate-600">{formatDate(order.expected_delivery)}</td>
                            <td className="px-4 py-4">
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                {order.status || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex justify-end gap-2">
                                <Link
                                  to={ROUTES.PO_DETAILS(order.po_id)}
                                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                >
                                  Open
                                </Link>
                                <Link
                                  to={ROUTES.PO_RECEIVE(order.po_id)}
                                  className="rounded-lg bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                                >
                                  Receive
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!loading && filteredOrders.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-slate-500">
                      No matching purchase orders found.
                    </div>
                  ) : null}
                </div>
              </Section>
            </div>
          )}

          {activeView === "raise" && (
            <Section
              title="Raise Purchase Order"
              subtitle="Create a new purchase order without leaving the Procurement Command Centre."
            >
              <NewPOForm refreshPOs={fetchPOs} />
            </Section>
          )}

          {activeView === "receiving" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <MetricCard
                  label="Due Today"
                  value={arrivingTodayCount}
                  hint={`${arrivingTodayMoney.value} · ${arrivingTodayMoney.hint}`}
                  icon={Truck}
                  tone="cyan"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=today`}
                />
                <MetricCard
                  label="Partial"
                  value={partialCount}
                  hint="Outstanding receiving balances"
                  icon={PackageOpen}
                  tone="rose"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=partial`}
                />
                <MetricCard
                  label="Completed"
                  value={receivedCount}
                  hint="Fully received purchase orders"
                  icon={CheckCircle2}
                  tone="emerald"
                  to={`${ROUTES.PO_RECEIVING_DASH}?filter=received`}
                />
              </div>

              <Section
                title="Receiving Operations"
                subtitle="Move directly into today's deliveries and outstanding balances."
              >
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <QuickAction
                    to={`${ROUTES.PO_RECEIVING_DASH}?filter=today`}
                    icon={Truck}
                    title="Today's Deliveries"
                    description="View purchase orders expected to arrive today."
                  />
                  <QuickAction
                    to={`${ROUTES.PO_RECEIVING_DASH}?filter=partial`}
                    icon={PackageOpen}
                    title="Partially Received"
                    description="Continue receiving outstanding balances."
                  />
                  <QuickAction
                    to={ROUTES.PO_RECEIVING_DASH}
                    icon={PackageCheck}
                    title="Receiving Dashboard"
                    description="Open the full receiving operational workspace."
                  />
                </div>
              </Section>
            </div>
          )}

          {activeView === "vendors" && (
            <div className="space-y-6">
              <Section
                title="Vendor Operations"
                subtitle="Supplier records and performance remain available from one focused workspace."
                right={
                  <Link
                    to={ROUTES.VENDORS}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Open Vendor Directory <ArrowRight size={15} />
                  </Link>
                }
              >
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <Building2 size={22} className="text-cyan-700" />
                    <h3 className="mt-4 font-semibold text-slate-950">Supplier Directory</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Review supplier records, status and operational contact data.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <BarChart3 size={22} className="text-cyan-700" />
                    <h3 className="mt-4 font-semibold text-slate-950">Performance</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Understand delivery reliability, rejection and fulfilment signals.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <FileText size={22} className="text-cyan-700" />
                    <h3 className="mt-4 font-semibold text-slate-950">Financial Exposure</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500">Move from supplier performance into invoice and payment exposure.</p>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {activeView === "intelligence" && canViewProcurementAI && (
            <div className="space-y-6">
              <Section
                title="AI Procurement Intelligence"
                subtitle="Focused risk detection and operational recommendations without crowding the main dashboard."
                right={
                  <Link
                    to={ROUTES.PROCUREMENT_AI_INSIGHTS}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900"
                  >
                    Open full AI view <ArrowRight size={15} />
                  </Link>
                }
              >
                <ProcurementAIInsightsPanel compact maxHeight="max-h-[620px]" />
              </Section>

              <Section
                title="Vendor Performance"
                subtitle="Live delivery and rejection indicators from procurement activity."
                right={
                  <Link to={ROUTES.VENDORS} className="text-sm font-semibold text-cyan-700 hover:text-cyan-900">
                    View vendors →
                  </Link>
                }
              >
                {vendorPerf.length === 0 ? (
                  <p className="text-sm text-slate-500">No performance data yet.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    <div className="h-72 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vendorPerf} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" />
                          <XAxis dataKey="vendor_name" stroke="#64748b" tick={{ fontSize: 12, fill: "#475569" }} />
                          <YAxis stroke="#64748b" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                          <Tooltip formatter={(v) => `${Math.round(v * 100)}%`} />
                          <Bar dataKey="on_time_rate" name="On-time" fill="#0891b2" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="full_delivery_rate" name="Full delivery" fill="#059669" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="h-72 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vendorPerf} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" />
                          <XAxis dataKey="vendor_name" stroke="#64748b" tick={{ fontSize: 12, fill: "#475569" }} />
                          <YAxis stroke="#64748b" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                          <Tooltip formatter={(v) => `${Math.round(v * 100)}%`} />
                          <Bar dataKey="rejection_rate" name="Rejection" fill="#ea580c" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="backorder_rate" name="Backorder" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </Section>
            </div>
          )}

          {activeView === "executive" && canViewGmCommand && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Link
                to={ROUTES.PROCUREMENT_GM_BRIEF}
                className="group overflow-hidden rounded-3xl border border-cyan-900/40 bg-slate-950 p-7 shadow-xl transition hover:-translate-y-0.5 hover:border-cyan-500/40"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-3 text-cyan-300">
                    <BrainCircuit size={24} />
                  </div>
                  <ArrowRight className="text-slate-600 transition group-hover:translate-x-1 group-hover:text-cyan-300" />
                </div>
                <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Executive briefing</p>
                <h2 className="mt-2 text-2xl font-semibold text-white">GM Procurement Brief</h2>
                <p className="mt-3 max-w-xl text-sm leading-6 text-slate-400">
                  One-click decision briefing covering late POs, blocked actions, invoice exposure,
                  vendor risk, cost-centre pressure and decisions requiring attention.
                </p>
              </Link>

              <Link
                to={ROUTES.PROCUREMENT_GM_ASSISTANT}
                className="group overflow-hidden rounded-3xl border border-emerald-900/40 bg-slate-950 p-7 shadow-xl transition hover:-translate-y-0.5 hover:border-emerald-500/40"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-3 text-emerald-300">
                    <Bot size={24} />
                  </div>
                  <ArrowRight className="text-slate-600 transition group-hover:translate-x-1 group-hover:text-emerald-300" />
                </div>
                <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">Executive assistant</p>
                <h2 className="mt-2 text-2xl font-semibold text-white">GM Procurement Assistant</h2>
                <p className="mt-3 max-w-xl text-sm leading-6 text-slate-400">
                  Ask live procurement questions in meetings about supplier risk, overdue exposure,
                  invoices, spend pressure and decision priorities.
                </p>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
